import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaHome,
  FaHospital,
  FaLeaf,
  FaArrowRight,
  FaCheckCircle,
  FaShieldAlt,
  FaGlobe,
  FaCogs,
  FaServer,
  FaPercent,
  FaClock,
  FaAward,
  FaRoute,
  FaTrashAlt,
  FaTemperatureLow
} from "react-icons/fa";
import "./giftcity.css";

const Giftcity = () => {
  const [activeSubTab, setActiveSubTab] = useState("office");

  // Framer Motion Animation Variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const scaleUp = {
    hidden: { opacity: 0, scale: 0.92 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
  };

  // Core Pillars Data
  const corePillars = [
    {
      title: "Premier IFSC Hub",
      icon: <FaGlobe />,
      color: "#007f6e",
      subtitle: "International Financial Services Centre",
      desc: "A special zone treated as a foreign jurisdiction under FEMA regulations. Conduct operations in key global currencies like USD, EUR, and GBP with zero domestic foreign exchange restrictions."
    },
    {
      title: "Next-Gen SEZ Zone",
      icon: <FaBuilding />,
      color: "#d49c18",
      subtitle: "Special Economic Zone benefits",
      desc: "State-of-the-art co-working and corporate spaces offering duty-free imports/exports, simplified regulatory clearances, and capital incentives under the latest Gujarat GCC Policy."
    },
    {
      title: "Smart Greenfield City",
      icon: <FaServer />,
      color: "#13294B",
      subtitle: "Future-Ready Greenfield Urbanization",
      desc: "Fully integrated plug-and-play smart city. Employs underground utility tunnels, automated pneumatic waste collection, and high-efficiency district cooling systems."
    }
  ];

  // Location Highlights Data
  const locationHighlights = [
    {
      title: "International Connectivity",
      info: "Ahmedabad International Airport is just a 20-minute drive, ensuring global business operations are effortlessly accessible.",
      icon: <FaRoute />
    },
    {
      title: "Seamless Metro Link",
      info: "Equipped with an expanding high-speed metro transit network that directly links GIFT City to Ahmedabad and Gandhinagar.",
      icon: <FaMapMarkerAlt />
    },
    {
      title: "Major Highway Access",
      info: "Strategically located along key expressways, connecting teams and logistics to major industrial hubs across Gujarat.",
      icon: <FaShieldAlt />
    }
  ];

  // Infrastructure Data
  const infraData = {
    office: {
      title: "World-Class Office Infrastructure",
      subtitle: "Custom spaces built to global corporate standards.",
      image: "./images/offices.webp",
      features: [
        { name: "Grade-A Office Buildings", desc: "Designed with modern layouts, high ceilings, and premium executive finishes." },
        { name: "Managed Office Spaces", desc: "Fully outsourced operations including receptionist, support staff, and facilities." },
        { name: "Co-working & Business Centers", desc: "Flexible desk plans, premium hot-desking, and dynamic community events." },
        { name: "Flexible Office Solutions", desc: "Customizable layouts that scale effortlessly alongside your corporate growth." },
        { name: "Smart Building Technology", desc: "Advanced access control, IoT energy tracking, and high-speed fiber internet." },
        { name: "Green-Certified Buildings", desc: "LEED/IGBC certified spaces prioritizing sustainable resources and energy efficiency." }
      ]
    },
    smartcity: {
      title: "Smart City Utilities",
      subtitle: "India's first fully integrated smart utility masterplan.",
      image: "./images/hero_bg.jpg",
      features: [
        { name: "Underground Utility Tunnels", desc: "Houses all power lines, fiber cables, and water pipes safely. No overhead wires or dug-up roads.", icon: <FaServer /> },
        { name: "District Cooling System (DCS)", desc: "Centralized cooling structure. Redefines air conditioning with 30%+ power savings.", icon: <FaTemperatureLow /> },
        { name: "Automated Waste Collection", desc: "Pneumatic waste disposal system. Trash is sucked underground at 90km/h directly to sorting plants.", icon: <FaTrashAlt /> },
        { name: "Smart Traffic & Security", desc: "CCTV surveillance with AI analytics, smart traffic management, and command control center.", icon: <FaShieldAlt /> },
        { name: "Sustainable Development", desc: "100% smart water meters, rainwater harvesting, and recycled water for city maintenance.", icon: <FaLeaf /> },
        { name: "Reliable Dual-Grid Power", desc: "99.999% uptime with dual-source power input and automatic switchover systems.", icon: <FaClock /> }
      ]
    },
    green: {
      title: "Sustainable & Green Initiatives",
      subtitle: "Blending modern business with environmental stewardship.",
      image: "./images/house.webp",
      features: [
        { name: "Zero Waste Discharge", desc: "Comprehensive recycling models ensuring minimal environmental impact." },
        { name: "Reduced Carbon Footprint", desc: "District cooling and smart transit significantly cut greenhouse gas emissions." },
        { name: "Water Conservation Systems", desc: "Water treatment plants recycling 100% of graywater for landscaping." },
        { name: "Green Landscape Planning", desc: "Expansive green corridors, tree-lined walking paths, and beautiful open spaces." },
        { name: "Energy-Efficient Architecture", desc: "Buildings engineered for thermal isolation, reducing active cooling demands." },
        { name: "Sustainable Mobility", desc: "Support for electric vehicles, public e-bikes, and zero-emission transit networks." }
      ]
    }
  };

  // Tax Benefits Data
  const taxBenefits = [
    {
      title: "100% Corporate Tax Holiday",
      desc: "Enjoy zero corporate tax for any 10 consecutive years out of a 15-year block under IFSC regulations.",
      icon: <FaPercent />
    },
    {
      title: "No Foreign Exchange Restrictions",
      desc: "Conduct business in multi-currencies (USD, EUR, GBP) since IFSC units are treated as foreign jurisdictions under FEMA.",
      icon: <FaGlobe />
    },
    {
      title: "Simplified Regulatory Framework",
      desc: "Operate under a single unified regulator (IFSCA), avoiding multiple overlapping regulatory clearances.",
      icon: <FaCogs />
    },
    {
      title: "Competitive Business Policies",
      desc: "Additional capital and operational expenditure incentives under Gujarat's specialized GCC Policy guidelines.",
      icon: <FaAward />
    }
  ];

  // Talent & Lifestyle Data
  const talentSkills = [
    "Software Developers", "Finance Professionals", "Banking Experts", "Cybersecurity Specialists",
    "Chartered Accountants", "Company Secretaries", "Legal Consultants", "Data Analysts",
    "Business Advisors", "AI & ML Engineers", "Asset Managers", "Risk Compliance Specialists"
  ];

  const lifestylePillars = [
    {
      title: "Excellent Residential Living",
      desc: "Relocated employees can enjoy premium apartments, family-friendly secure neighborhoods, and modern smart utility grids within the city.",
      icon: <FaHome />,
      image: "./images/house.webp"
    },
    {
      title: "Top Educational Institutions",
      desc: "Access reputed international schools, IB & CBSE curriculums, quality daycares, and elite higher education campuses nearby.",
      icon: <FaGraduationCap />,
      image: "./images/schools.webp"
    },
    {
      title: "Healthcare & Wellness",
      desc: "Equipped with state-of-the-art multi-specialty hospitals, emergency clinics, pharmacies, wellness centers, and premium fitness clubs.",
      icon: <FaHospital />,
      image: "./images/offices.webp"
    }
  ];

  // Matrix Comparison Data
  const matrixData = [
    {
      advantage: "Strategic Location",
      benefit: "Centrally positioned between Ahmedabad and Gandhinagar, offering high-speed highway and upcoming metro transit links.",
      highlight: "20 Mins to Airport"
    },
    {
      advantage: "Global Infrastructure",
      benefit: "Grade-A office towers, centralized district cooling, automated waste system, and premium smart utilities.",
      highlight: "Plug-and-Play Offices"
    },
    {
      advantage: "Business-Friendly Policies",
      benefit: "Streamlined single-window clearance, simplified entity creation, and ongoing regulatory guidance.",
      highlight: "IFSCA Unified Regulator"
    },
    {
      advantage: "IFSC Ecosystem",
      benefit: "Direct entry to international financial transactions in major foreign currencies under FEMA guidelines.",
      highlight: "Treated as Offshore"
    },
    {
      advantage: "Skilled Workforce",
      benefit: "High concentration of top-tier talent in IT, finance, tax, legal, and software engineering due to local university pipelines.",
      highlight: "Continuous Pipeline"
    },
    {
      advantage: "Premium Living Options",
      benefit: "Safe, walkable, green communities with modern apartments, eliminating stressful daily commutes.",
      highlight: "Walk-to-Work Concept"
    },
    {
      advantage: "Quality Education",
      benefit: "Reputed schools (CBSE, ICSE, IB, International) and daycare options within a convenient radius.",
      highlight: "IB & CBSE Curriculums"
    },
    {
      advantage: "Modern Healthcare",
      benefit: "Full access to specialized medical clinics, emergency services, pharmacies, and premium wellness facilities.",
      highlight: "Multi-Specialty Care"
    },
    {
      advantage: "Smart City Infrastructure",
      benefit: "Underground utility tunnels prevent overhead service outages. Advanced smart-grid water and electricity monitoring.",
      highlight: "Zero Overhead Outages"
    },
    {
      advantage: "Future Growth Support",
      benefit: "Rapidly expanding ecosystem backed by government initiatives, connecting startups and global capability centers.",
      highlight: "High Long-Term ROI"
    }
  ];

  // How We Help You Data (Roadmap)
  const servicesSteps = [
    {
      step: "01",
      title: "Business Setup Consultation",
      desc: "Expert analysis to evaluate licensing, entity selection, and compliance requirements under IFSC guidelines."
    },
    {
      step: "02",
      title: "Office Space Selection",
      desc: "Identifying and lease negotiations for Grade-A offices, managed spaces, or flexible co-working hubs."
    },
    {
      step: "03",
      title: "Registration & Compliance",
      desc: "End-to-end support for IFSCA filings, SEZ registrations, company incorporation, and local government compliance."
    },
    {
      step: "04",
      title: "Employee Relocation Support",
      desc: "Assisting teams with residential rental selections, local utilities connection, school enrollment, and banking support."
    },
    {
      step: "05",
      title: "Ongoing Operational Support",
      desc: "Connecting you to verified local networks, interior decorators, hardware suppliers, and operational services."
    }
  ];

  return (
    <div className="giftcity-page-wrapper">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="giftcity-hero-section">
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={7}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="hero-text-content"
              >
                <span className="hero-badge-tag">INDIA'S GLOBAL DESTINATION</span>
                <h1 className="hero-title">
                  Establish, Operate, and Scale in <span>GIFT City</span>
                </h1>
                <p className="hero-description">
                  GIFT City (Gujarat International Finance Tec-City) is India's first operational smart city and home to the country's International Financial Services Centre (IFSC). Designed to meet global standards, GIFT City offers businesses a world-class ecosystem with modern infrastructure, business-friendly regulations, advanced technology, and an exceptional quality of life.
                </p>
                <p className="hero-subtext">
                  Whether you are a startup, fintech company, multinational corporation, Global Capability Centre (GCC), or professional services firm, GIFT City provides the ideal environment to establish and expand your global footprint.
                </p>
                <div className="hero-actions mt-4 d-flex flex-wrap gap-3">
                  <a href="#setup-roadmap" className="cta-primary-btn">
                    Get Free Setup Consult <FaArrowRight />
                  </a>
                  <a href="#advantage-matrix" className="cta-secondary-btn">
                    View Business Matrix
                  </a>
                </div>
              </motion.div>
            </Col>
            <Col lg={5}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={scaleUp}
                className="hero-interactive-stats"
              >
                <div className="stats-glass-card shadow-lg">
                  <div className="stat-item">
                    <h3>
                      <CountUp end={100} duration={2} suffix="%" />
                    </h3>
                    <p>Corporate Tax Holiday (10 Yrs)</p>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-item">
                    <h3>
                      <CountUp end={20} duration={2} suffix=" Mins" />
                    </h3>
                    <p>To Ahmedabad Airport</p>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-item">
                    <h3>
                      <CountUp end={0} duration={2} prefix="" suffix=" FX" />
                    </h3>
                    <p>Mainland Currency Restrictions</p>
                  </div>
                  <div className="stat-decorator-ring"></div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Core Highlights Pillars Section */}
      <section className="core-pillars-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">GLOBAL ECOSYSTEM</span>
            <h2>Why Businesses Choose GIFT City</h2>
            <p className="section-description">
              A next-generation business hub that combines cutting-edge infrastructure with a supportive regulatory environment, bringing together financial institutions, technology leaders, and global enterprises.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="pillars-grid mt-5"
          >
            <Row className="g-4">
              {corePillars.map((pillar, idx) => (
                <Col lg={4} key={idx}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -8, scale: 1.02 }}
                    className="pillar-card"
                  >
                    <div className="pillar-decor-line" style={{ background: pillar.color }}></div>
                    <div className="pillar-icon-box" style={{ color: pillar.color, background: `${pillar.color}15` }}>
                      {pillar.icon}
                    </div>
                    <h4>{pillar.title}</h4>
                    <span className="pillar-subtitle">{pillar.subtitle}</span>
                    <p>{pillar.desc}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Strategic Location & Connectivity */}
      <section className="location-connectivity-section">
        <div className="decor-glow-left"></div>
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="location-text-box"
              >
                <span className="section-subtitle-badge light">STRATEGIC GEOGRAPHY</span>
                <h2>Perfectly Positioned for Connectivity</h2>
                <p className="location-desc">
                  Located strategically between **Ahmedabad** (Gujarat's commercial capital) and **Gandhinagar** (the state capital), GIFT City offers excellent regional and international connectivity through highways, airports, and expanding rail networks.
                </p>
                <div className="location-accent-card mt-4">
                  <h5>High-Speed Metro Connection</h5>
                  <p>The upcoming high-speed metro corridor directly links GIFT City commercial towers to key residential clusters and retail spaces in Ahmedabad.</p>
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
                className="location-cards-box"
              >
                <Row className="g-3">
                  {locationHighlights.map((hl, idx) => (
                    <Col md={12} key={idx}>
                      <motion.div variants={fadeInUp} className="location-hl-card d-flex align-items-start gap-4">
                        <div className="location-hl-icon-box">
                          {hl.icon}
                        </div>
                        <div>
                          <h5>{hl.title}</h5>
                          <p>{hl.info}</p>
                        </div>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Tax & Regulatory Incentives Panel */}
      <section className="tax-regulatory-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">OFFSHORE JURISDICTION</span>
            <h2>Tax & Regulatory Benefits</h2>
            <p className="section-description">
              Eligible businesses operating within the International Financial Services Centre (IFSC) framework benefit from attractive tax exemptions and relaxed exchange controls.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="tax-cards-grid mt-5"
          >
            <Row className="g-4">
              {taxBenefits.map((benefit, idx) => (
                <Col md={6} lg={3} key={idx}>
                  <motion.div variants={fadeInUp} className="tax-card h-100 text-center">
                    <div className="tax-icon-wrapper">
                      {benefit.icon}
                    </div>
                    <h5>{benefit.title}</h5>
                    <p>{benefit.desc}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>

          <p className="tax-disclaimer text-center mt-4">
            *Tax benefits and incentives depend on your specific business activity, regulatory approvals, and prevailing government policies.
          </p>
        </Container>
      </section>

      {/* Dual Infrastructure Tab Showcase */}
      <section className="infrastructure-showcase-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">FUTURISTIC UTILITIES</span>
            <h2>World-Class Smart Infrastructure</h2>
            <p className="section-description">
              GIFT City is built from scratch on greenfield land using advanced urban planning frameworks to offer seamless, uninterrupted corporate operations.
            </p>
          </div>

          {/* Interactive Sub-Tabs Controller */}
          <div className="custom-sub-tabs-container d-flex justify-content-center mt-5 mb-4">
            <div className="custom-tab-navbar d-flex gap-2">
              <button
                className={activeSubTab === "office" ? "sub-tab-btn active" : "sub-tab-btn"}
                onClick={() => setActiveSubTab("office")}
              >
                <FaBuilding /> Office Infrastructure
              </button>
              <button
                className={activeSubTab === "smartcity" ? "sub-tab-btn active" : "sub-tab-btn"}
                onClick={() => setActiveSubTab("smartcity")}
              >
                <FaServer /> Smart City Utilities
              </button>
              <button
                className={activeSubTab === "green" ? "sub-tab-btn active" : "sub-tab-btn"}
                onClick={() => setActiveSubTab("green")}
              >
                <FaLeaf /> Green Development
              </button>
            </div>
          </div>

          {/* Tab Content Rendering with Animations */}
          <div className="infra-content-wrapper">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSubTab}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4 }}
              >
                <Row className="align-items-center gy-5">
                  <Col lg={6}>
                    <div className="infra-info-card">
                      <h3>{infraData[activeSubTab].title}</h3>
                      <p className="infra-subtitle-text">{infraData[activeSubTab].subtitle}</p>
                      <div className="infra-features-list mt-4">
                        <Row className="g-3">
                          {infraData[activeSubTab].features.map((feat, idx) => (
                            <Col sm={12} key={idx}>
                              <div className="infra-feature-item d-flex gap-3 align-items-start">
                                <div className="feat-check"><FaCheckCircle /></div>
                                <div>
                                  <h6>{feat.name}</h6>
                                  <p>{feat.desc}</p>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="infra-image-container">
                      <img src={infraData[activeSubTab].image} alt={infraData[activeSubTab].title} className="img-fluid rounded-4 shadow-lg" />
                      <div className="infra-image-badge">Smart City Blueprint</div>
                    </div>
                  </Col>
                </Row>
              </motion.div>
            </AnimatePresence>
          </div>
        </Container>
      </section>

      {/* Talent & Lifestyle (Quality of Life) */}
      <section className="talent-lifestyle-section">
        <Container>
          {/* Access to Talent Header */}
          <div className="talent-header-block mb-5">
            <Row className="align-items-center gy-4">
              <Col lg={6}>
                <span className="section-subtitle-badge">GLOBAL TALENT</span>
                <h2>Access to World-Class Talent</h2>
                <p className="talent-para">
                  GIFT City acts as a magnet for top professionals from across India. In addition, major universities and vocational training hubs in close proximity ensure a steady flow of high-quality graduates across diverse disciplines.
                </p>
              </Col>
              <Col lg={6}>
                <div className="talent-tags-container d-flex flex-wrap gap-2 justify-content-lg-end">
                  {talentSkills.map((skill, idx) => (
                    <span className="skill-pill" key={idx}>
                      {skill}
                    </span>
                  ))}
                </div>
              </Col>
            </Row>
          </div>

          <div className="divider-line my-5"></div>

          {/* Lifestyle Features Grid */}
          <div className="lifestyle-header text-center mb-5">
            <span className="section-subtitle-badge">LIFESTYLE & CIVIC INFRASTRUCTURE</span>
            <h2>Better Quality of Life for Relocated Teams</h2>
            <p className="section-description">
              Relocating employees enjoy a modern, premium lifestyle, safe walkable communities, and excellent civic facilities that support families and continuous personal growth.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="lifestyle-grid"
          >
            <Row className="g-4">
              {lifestylePillars.map((item, idx) => (
                <Col lg={4} md={6} key={idx}>
                  <motion.div variants={fadeInUp} className="lifestyle-card h-100">
                    <div className="lifestyle-image-box">
                      <img src={item.image} alt={item.title} />
                      <div className="lifestyle-icon-overlay">
                        {item.icon}
                      </div>
                    </div>
                    <div className="lifestyle-content-box">
                      <h5>{item.title}</h5>
                      <p>{item.desc}</p>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Growing Business Community & Industries Badges */}
      <section className="business-community-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge light">THRIVING ENVIRONMENT</span>
            <h2>Growing Business Community</h2>
            <p className="section-description">
              By establishing your office in GIFT City, you become part of a rapidly expanding network of national and international businesses operating at scale.
            </p>
          </div>

          <div className="industries-badges-cloud mt-5 text-center">
            {[
              "Banking", "Financial Services", "Insurance", "FinTech", "Information Technology",
              "Consulting", "Global Capability Centres (GCCs)", "Professional Services",
              "Investment Firms", "Asset Management", "Legal Services"
            ].map((industry, idx) => (
              <span className="industry-badge-tag" key={idx}>
                {industry}
              </span>
            ))}
          </div>
        </Container>
      </section>

      {/* Why Choose GIFT City SUMMARY GRID (Advantage Matrix) */}
      <section id="advantage-matrix" className="advantage-matrix-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">AT A GLANCE</span>
            <h2>Why Choose GIFT City for Your Business?</h2>
            <p className="section-description">
              A comprehensive summary of direct business advantages and lifestyle benefits available under IFSC guidelines.
            </p>
          </div>

          <div className="matrix-table-wrapper mt-5 shadow-lg rounded-4 overflow-hidden">
            <div className="matrix-grid-header d-none d-md-flex">
              <div className="grid-header-col col-advantage">Business Advantage</div>
              <div className="grid-header-col col-benefit">Benefit & Operational Value</div>
              <div className="grid-header-col col-highlight text-end">Key Highlight</div>
            </div>

            <div className="matrix-grid-body">
              {matrixData.map((row, idx) => (
                <div className="matrix-grid-row d-flex flex-column flex-md-row align-items-start align-items-md-center" key={idx}>
                  <div className="grid-body-col col-advantage">
                    <span className="mobile-label d-md-none">Advantage</span>
                    <h5>{row.advantage}</h5>
                  </div>
                  <div className="grid-body-col col-benefit">
                    <span className="mobile-label d-md-none">Benefit</span>
                    <p>{row.benefit}</p>
                  </div>
                  <div className="grid-body-col col-highlight text-md-end">
                    <span className="mobile-label d-md-none">Highlight</span>
                    <span className="badge-highlight">{row.highlight}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* How We Help You (Setup Roadmap) */}
      <section id="setup-roadmap" className="setup-roadmap-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">COMPLETE SUPPORT</span>
            <h2>How We Help You</h2>
            <p className="section-description">
              Setting up your office in GIFT City involves more than finding office space. We provide complete, end-to-end support throughout your setup and relocation journey.
            </p>
          </div>

          <div className="roadmap-flow mt-5">
            <Row className="g-4 justify-content-center">
              {servicesSteps.map((step, idx) => (
                <Col lg={4} md={6} key={idx}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={fadeInUp}
                    className="roadmap-step-card"
                  >
                    <div className="step-num">{step.step}</div>
                    <h5>{step.title}</h5>
                    <p>{step.desc}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </section>

      {/* Build Your Future CTA Section */}
      <section className="giftcity-cta-section text-center">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleUp}
            className="cta-glass-box shadow-2xl"
          >
            <h2>Build Your Future in GIFT City</h2>
            <p className="cta-paragraph">
              Whether you are launching a high-growth startup, expanding an established enterprise, or setting up a dedicated Global Capability Centre (GCC), GIFT City offers the infrastructure, ecosystem, and opportunities to support your long-term success.
            </p>
            <p className="cta-sub-paragraph">
              With our end-to-end guidance, you can establish your business confidently while we take care of the setup, entity registration, property selection, compliance, and relocation logistics—so you can focus entirely on growth.
            </p>
            <div className="cta-btn-group mt-4">
              <a href="/contactus" className="btn-cta-primary">
                Contact Our Gift City Experts
              </a>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Giftcity;
